import {
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import type {
  MiseSeller,
  MiseSellerGroup,
  MiseSellerSellerGroupMap,
} from '@/lib/types/mise/seller';
import { useMemo } from 'react';

interface SellerSellerGroupMapListProps {
  mappings: MiseSellerSellerGroupMap[] | undefined;
  sellers: MiseSeller[] | undefined;
  sellerGroups: MiseSellerGroup[] | undefined;
  isLoading: boolean;
  error: Error | null;
  title?: string;
  unmappedSellers?: MiseSeller[];
}

export function SellerSellerGroupMapList({
  mappings,
  sellers,
  sellerGroups,
  isLoading,
  error,
  title,
  unmappedSellers,
}: SellerSellerGroupMapListProps) {
  const sellerMap = useMemo(() => new Map(sellers?.map((s) => [s.id, s.name]) ?? []), [sellers]);

  const sellerGroupMap = useMemo(
    () => new Map(sellerGroups?.map((g) => [g.id, g.name]) ?? []),
    [sellerGroups],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error instanceof Error ? error.message : 'マッピングの読み込みに失敗しました。'}
      </Alert>
    );
  }

  if (!mappings || mappings.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        マッピングが登録されていません。
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {title && (
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          {title}
        </Typography>
      )}
      {unmappedSellers && unmappedSellers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            以下の店舗がマッピングに含まれていません（{unmappedSellers.length}件）
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '1.5em' }}>
            {unmappedSellers.map((s) => (
              <li key={s.id}>
                {s.name}（{s.id}）
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>店舗ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>店舗名</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>店舗グループID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>店舗グループ名</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping) => (
              <TableRow key={`${mapping.seller_id}-${mapping.seller_group_id}`} hover>
                <TableCell sx={{ wordBreak: 'break-all' }}>{mapping.seller_id}</TableCell>
                <TableCell>{sellerMap.get(mapping.seller_id) ?? ''}</TableCell>
                <TableCell sx={{ wordBreak: 'break-all' }}>{mapping.seller_group_id}</TableCell>
                <TableCell>{sellerGroupMap.get(mapping.seller_group_id) ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        全{mappings.length}件
      </Typography>
    </Box>
  );
}
